/* eslint-disable no-console */
/* eslint-disable no-await-in-loop */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'seamless-immutable';
import { Auth, API } from 'aws-amplify';
import Bugsnag from '@bugsnag/js';
import _ from 'lodash';
import LanguageInitialization from './LanguageInitialization';

import { hasUpperGroups, hasAllowedGroups } from '../../data/groups';
import { handleSessionLost, isSupersededSession } from '../../utils/session';
import { getUserRole } from '../../data';

import { USERS } from '../../graphql';
import { enhancedQuery } from '../../utils/graphql';

import { clientUser } from '../../index';

import AuthContext, { initialState } from './context';
import { SnackbarContext } from '../Snackbar';

import { redirectLogin } from '../../utils';
import { LOCAL_AUTH_SHIM, LOCAL_USER_ID, LOCAL_SESSION_ID } from '../../utils/authShim';
import {
  getAuthentication,
} from '../../data/mhub_api';
import config from '../../config';
import { LoadingPage } from '../../components';
import AccessDenied from '../../pages/AccessDenied';
import { countryFeatures } from '../../localization/features/countryFeatures';
import { COUNTRY_MY } from '../../localization/features/constants';
import { deleteCookie, deleteStaleAuthCookies } from '../../utils/cookies';

const formatPhoneNumber = (phoneNumber) => {
  if (phoneNumber) {
    return phoneNumber.replace(/\+/g, '');
  }
  return '';
};

const isExternalCompanyBranch = (list, id) => (
  list[id]
  && list[id].is_external
  && list[id].is_external.toLowerCase() === 'true'
);

const getUserData = (resp) => {
  let user = {};
  const affiliate = resp.affiliate || {};
  const company = (affiliate && affiliate.company) || {};
  const companyBranch = (affiliate && affiliate.companyBranch) || {};
  const groups = resp.groups.map((group) => group.code) || [];
  if (!_.isEmpty(resp)) {
    user = {
      id: resp.awsUsername,
      name: resp.name,
      preferred_name: resp.preferredName,
      email: resp.email,
      mobile_prefix: resp.mobilePrefix,
      mobile_suffix: resp.mobile,
      phone_number: `${resp.mobilePrefix}${resp.mobile}`,
      groups,
      role: getUserRole(
        groups,
        companyBranch.isExternal,
      ),
      company: {
        id: company.service_company_id,
        name: company.name,
        shortName: company.shortName,
        apps: company.apps,
        code: company.code,
        activeLanguageCodes: company.activeLanguageCodes,
      },
      companyBranch: {
        id: companyBranch.service_company_branch_id,
        name: companyBranch.name,
        shortName: companyBranch.shortName,
        isExternal: companyBranch.isExternal,
      },
    };
  }
  Bugsnag.addMetadata('user', {
    user_id: user.id,
    name: user.name,
    email: user.email,
    company_id: user.company.id,
  });
  return user;
};

const getAllCognitoCookies = (base) => document.cookie.split('; ')
  .filter((cookie) => cookie.startsWith(base))
  .reduce((acc, cookie) => {
    const [key, value] = cookie.split('=');
    return { ...acc, [decodeURIComponent(key)]: decodeURIComponent(value) }; // Decode cookies
  }, {});

const getUserList = (users, listCompanyBranches, convertToObject) => {
  // Set users as object with userId as key
  // For accessing user info by user id
  if (convertToObject) {
    const mapped = users.map((u) => {
      const companyBranchId = u.company_branch_id;
      const companyBranchName = (listCompanyBranches[companyBranchId] && listCompanyBranches[companyBranchId].name) || '';
      const isExternal = isExternalCompanyBranch(listCompanyBranches, companyBranchId);
      return ({
        [u.id]: {
          ...u,
          role: getUserRole(u.groups, isExternal),
          company_branch_name: companyBranchName,
          company_branch_id: companyBranchId,
        },
      });
    });
    const objects = Object.assign({}, ...mapped);
    return objects;
  }

  const options = users.map((u) => {
    const companyBranchId = u.company_branch_id;
    const companyBranchName = (listCompanyBranches[companyBranchId] && listCompanyBranches[companyBranchId].name) || '';
    const isExternal = isExternalCompanyBranch(listCompanyBranches, companyBranchId);
    return ({
      label: u.name,
      value: u.id,
      role: getUserRole(u.groups, isExternal),
      company_branch_name: companyBranchName,
      company_branch_id: companyBranchId,
      ...u,
    });
  });
  return options;
};

const setCurrentSession = async () => {
  // LOCAL-DEV ONLY: offline there are no CognitoIdentityServiceProvider.* cookies, so the lookup
  // below throws, the caller burns its retries and redirects to /logout. The user_sessions_local
  // row this endpoint would create is already seeded by each stack's local/seed.sh.
  // No-op unless REACT_APP_LOCAL_AUTH_SHIM=1.
  if (LOCAL_AUTH_SHIM) {
    return { user_id: LOCAL_USER_ID, session_token_id: LOCAL_SESSION_ID };
  }

  const baseCookie = `CognitoIdentityServiceProvider.${process.env.REACT_APP_COGNITO_APP_CLIENT_ID}`;
  const cognitoCookies = getAllCognitoCookies(baseCookie);
  const idTokenKey = Object.keys(cognitoCookies).find((key) => key.endsWith('.idToken'));
  if (!idTokenKey) {
    const customError = new Error('CognitoIdentityServiceProvider.idToken not found');
    Bugsnag.notify(customError, {
      metadata: {
        cookies: cognitoCookies,
      },
    });
    throw customError;
  }
  try {
    const response = await API.post(
      'USER',
      '/usersession/create',
      {
        body: {
          id_token: cognitoCookies[idTokenKey],
          redirect_url: process.env.REACT_APP_MHUB_LEAD_URL,
          request_headers: cognitoCookies,
        },
      },
    );
    return response;
  } catch (error) {
    // A superseded session is an EXPECTED outcome, not a fault: the user signed in
    // elsewhere and this tab lost the single-session slot. mhub-user-api answers 403 for
    // it, so retrying or paging anyone would be wrong — it is neither retried (see this
    // function's caller) nor sent to Bugsnag, which was filing one report per attempt.
    if (!isSupersededSession(error)) {
      Bugsnag.notify(error, {
        metadata: {
          cookies: cognitoCookies,
        },
      });
    }
    console.error('Error creating session:', error);
    throw error;
  }
};

class AuthProvider extends Component {
  static Consumer = AuthContext.Consumer;

  static initialState = initialState;

  constructor(props) {
    super(props);
    this.state = {
      data: initialState,
    };
  }

  componentDidMount() {
    this.authenticate();
  }

  componentWillUnmount() {
    const { actions } = this.context;
    const { setCloseSnackbar } = actions;
    setCloseSnackbar();
  }

  setStateAsync = (newState = {}) => new Promise((resolve) => {
    this.setState((prevState) => ({
      data: Immutable.merge(prevState.data, newState),
    }), () => resolve(newState));
  });

  fetchAllUsers = async () => {
    const { actions } = this.context;
    const { setOpenSnackbar } = actions;
    return enhancedQuery(clientUser, USERS.LIST(), {
      options: {
        variables: {
          company_branch_id: '',
        },
        onError: () => {
          setOpenSnackbar({
            variant: 'error',
            message: 'An error has occured. Please try again.',
          });
        },
      },
      defaultData: {
        listUsers: [],
      },
      selector: (d) => d.listUsers,
    });
  };

  fetchUsers = async (user) => {
    const { actions } = this.context;
    const { setOpenSnackbar } = actions;
    return enhancedQuery(clientUser, USERS.LIST(), {
      options: {
        variables: {
          company_branch_id: ['Supervisor', 'Sales Manager', 'Sales Admin'].includes(user.role) ? user.companyBranch.id : '',
        },
        onError: () => {
          setOpenSnackbar({
            variant: 'error',
            message: 'An error has occured. Please try again.',
          });
        },
      },
      defaultData: {
        listUsers: [],
      },
      selector: (d) => d.listUsers,
    });
  };

  getAllUsers = async () => {
    const { users } = await this.fetchAllUsers();
    if (!users) {
      return [];
    }

    return users.map((u) => {
      if (u.phone_number) {
        return {
          ...u,
          phone_number: formatPhoneNumber(u.phone_number),
        };
      }
      return u;
    });
  };

  getUsers = async (user) => {
    const { users } = await this.fetchUsers(user);
    if (!users) {
      return [];
    }

    return users.map((u) => {
      if (u.phone_number) {
        return {
          ...u,
          phone_number: formatPhoneNumber(u.phone_number),
        };
      }
      return u;
    });
  };

  fetchListCompanyBranch = async () => {
    const { actions } = this.context;
    const { setOpenSnackbar } = actions;
    let list = await enhancedQuery(clientUser, USERS.LIST_COMPANY_BRANCH(), {
      options: {
        onError: () => {
          setOpenSnackbar({
            variant: 'error',
            message: 'An error has occured. Please try again.',
          });
        },
      },
      defaultData: {
        listCompanyBranches: [],
      },
      selector: (d) => d.listCompanyBranches,
    });
    list = list.company_branches || [];
    const mapped = list.map((bu) => ({ [bu.id]: bu }));
    return Object.assign({}, ...mapped);
  };

  getCurrentSession = async (firstTry = true) => {
    let resp;
    try {
      const authUser = await Auth.currentAuthenticatedUser();
      resp = authUser.getSignInUserSession();
    } catch (error) {
      if (
        firstTry && (
          (typeof error === 'string' && !error.match(/not authenticate/i)) || (
            typeof error === 'object' && !Array.isArray(error) && error.code !== 'PasswordResetRequiredException'
            && (error.message.match(/not authenticate/i)
              || error.message.match(/token/i))
          )
        )
      ) {
        // delay retry to check new token
        await setTimeout(() => {
          this.getCurrentSession(false);
        }, 1000);
        return null;
      }

      await this.setStateAsync({
        hasAccess: false,
        isAuthenticated: false,
        isLoading: false,
      });
      return null;
    }
    return resp;
  };

  authenticate = async () => {
    await this.setStateAsync({
      isLoading: true,
      hasAccess: false,
      isAuthenticated: false,
      accessDenied: null,
    });

    const sessionResp = await this.getCurrentSession();

    let { hasAccess, hasHigherAccess } = this.state;

    let isAuthenticated = false;
    let user = {
      groups: [],
    };
    let data = {};

    if (sessionResp) {
      // Clean up stale Cognito cookies from previous users/sessions to prevent
      // request headers from exceeding the server's maximum allowed size.
      try {
        const currentUserId = sessionResp.idToken.payload['cognito:username'] || sessionResp.idToken.payload.sub;
        deleteStaleAuthCookies(
          process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
          currentUserId,
          config.cognito.cookieDomain,
        );
      } catch (e) {
        // Non-critical — proceed even if cleanup fails
      }

      data = await getAuthentication();
      if (data && data.forbidden) {
        // LMS-5174: the organisation is suspended. The Cognito session is valid, so sending
        // the user to the login app would only bounce them back here in a loop. Show the
        // Access Denied page instead (rendered straight from this provider — every
        // authenticated route would otherwise redirect an unauthenticated state to /login).
        await this.setStateAsync({
          isAuthenticated: false,
          hasAccess: false,
          hasHigherAccess: false,
          isLoading: false,
          accessDenied: { message: data.message || '' },
        });
        return;
      }
      if (data && !_.isEmpty(data)) {
        user = await getUserData(data);
        if (user && !_.isEmpty(user)) {
          isAuthenticated = true;
        }
      }
    }

    // FIXME: Implement this in separate context provider
    let apps = null;
    let country = {};
    let users = {};
    let userOptions = [];
    let activeUserOptions = [];
    let listCompanyBranches = [];

    const maxRetries = 2; // Number of retries after the first attempt
    let attempts = 0;
    let success = false;

    if (isAuthenticated && Array.isArray(user.groups) && user.groups.length > 0) {
      while (attempts <= maxRetries && !success) {
        try {
          await setCurrentSession();
          success = true;
        } catch (error) {
          attempts += 1;
          console.error(`Attempt ${attempts} failed:`, error);

          // A superseded session is terminal: another sign-in owns the slot, so every
          // retry is refused too and the 3s waits only delay the sign-out by ~6s. Hand
          // straight to the login app, which is how every other product handles a lost
          // session (mhub-post-sales-web's signOut is exactly this redirect). Keeping the
          // flow in one place keeps the copy and the takeover story owned by secure.
          if (isSupersededSession(error) || attempts > maxRetries) {
            // Same hand-off as the mid-session path, so a takeover discovered at BOOTSTRAP explains
            // itself too. It previously redirected to a bare /logout, which left exactly the users
            // most likely to be confused — the ones whose session was taken while they
            // were away — staring at a sign-in page with no reason given.
            //
            // Only a genuine supersede carries the reason. Exhausted retries reach here
            // too, and that is a network or API failure: telling those users someone
            // signed in elsewhere would be a fabricated explanation.
            if (isSupersededSession(error)) {
              handleSessionLost(config.app.secureUrl);
            } else {
              window.location.href = `${config.app.secureUrl}/logout`;
            }
            await this.setStateAsync({
              authenticated: false,
              hasAccess: false,
              hasHigherAccess: false,
              isLoading: false,
              isLoggedOut: true,
            });
            break;
          }
          await new Promise((resolve) => { setTimeout(resolve, 3000); });
        }
      }

      if (success) {
        listCompanyBranches = await this.fetchListCompanyBranch();

        hasAccess = hasAllowedGroups(user.groups);
        hasHigherAccess = hasUpperGroups(user.groups);

        // Retrieve user list
        const listUsers = await this.getUsers(user);
        const listAllUsers = await this.getAllUsers();

        // Set user options
        // Exclude users without name and sort users in ascending order
        const filtered = listUsers.filter((u) => u.name);
        filtered
          .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }))
          .sort((a, b) => {
            if (!(a && a.user_status) || a.user_status === 'suspended') {
              return 1;
            }
            if (!(b && b.user_status) || b.user_status === 'suspended') {
              return -1;
            }
            return 0;
          });
        userOptions = await getUserList(filtered, listCompanyBranches);
        users = await getUserList(listAllUsers, listCompanyBranches, true);

        const userIds = Object.values(users).map((u) => (u.id));
        if (userIds && userIds.length) {
          const userExist = userIds.filter((id) => id === user.id).length > 0;
          if (!userExist) {
            users[user.id] = user;
            Bugsnag.notify(`User ${user} not found in user listing`);
          }
        }

        // Retrieve only active users
        const filteredNonActive = filtered.filter((u) => u.user_status && u.user_status !== 'suspended');
        activeUserOptions = await getUserList(filteredNonActive, listCompanyBranches);

        // Get showroom app details
        if (data && data.affiliate && data.affiliate.company && data.affiliate.company.apps) {
          const baseCountry = countryFeatures[data.affiliate.company.countryCode]
          || countryFeatures[COUNTRY_MY];
          const icRegex = baseCountry.ic_validation_regex;
          country = {
            ...baseCountry,
            ic_validation_regex: icRegex instanceof RegExp ? icRegex.source : (icRegex || ''),
            country_code: data.affiliate.company.countryCode || baseCountry.country_code || '',
            currency_code: data.affiliate.company.currencyCode || baseCountry.currency || '',
            locale_code: baseCountry.locale_code || 'en-US',
          };
          apps = data.affiliate.company.apps
            .reduce((obj, app) => Object.assign(obj, { [app.appID]: app }), {});
        }
      }
    } else {
      window.location.href = `${config.app.secureUrl}${redirectLogin()}`;
    }

    await this.setStateAsync({
      apps,
      country,
      hasAccess,
      hasHigherAccess,
      isAuthenticated,
      user,
      users,
      userOptions,
      activeUserOptions,
      listCompanyBranches,
      isLoading: false,
    });
  };

  destroyCurrentSession = async () => {
    try {
      const sessionResp = await this.getCurrentSession();
      const baseCookie = `CognitoIdentityServiceProvider.${process.env.REACT_APP_COGNITO_APP_CLIENT_ID}.${sessionResp.idToken.payload.sub}`;
      const cognitoCookies = getAllCognitoCookies(baseCookie);
      const idToken = cognitoCookies[`${baseCookie}.idToken`];

      const userSessionResp = await API.post(
        'USER',
        '/usersession/delete',
        { body: { id_token: idToken } },
      );
      return userSessionResp;
    } catch (error) {
      console.error('Error destroying session:', error);
      throw error;
    } finally {
      deleteCookie('preferred_language');
      deleteCookie('mhub_profile_language_v1');
    }
  };

  // Sign out of cognito session.
  // Redirect to login app's signout page to let login app handle signout session flow.
  signOut = async () => {
    try {
      await this.destroyCurrentSession();
      window.location.href = `${config.app.secureUrl}/logout`;
      await this.setStateAsync({
        authenticated: false,
        hasAccess: false,
        hasHigherAccess: false,
        isLoading: false,
        isLoggedOut: true,
      });
    } catch (error) {
      console.error('Error during sign-out:', error);
    }
  };

  render() {
    const { children } = this.props;
    const { data } = this.state;
    // Memoize value to avoid unnecessary re-renders
    if (!this._memoizedValue || this._lastData !== data) {
      this._memoizedValue = {
        state: data,
        actions: {
          authenticate: this.authenticate,
          signOut: this.signOut,
        },
      };
      this._lastData = data;
    }
    const value = this._memoizedValue;

    if (data.accessDenied) {
      // LMS-5174: suspended organisation — no login redirect, no app shell.
      return <AccessDenied message={data.accessDenied.message} />;
    }

    if (!data.isAuthenticated) {
      return <LoadingPage isLoading />;
    }

    return (
      <AuthContext.Provider value={value}>
        <LanguageInitialization userId={data.user?.id} countryCode={data.country?.country_code} />
        {children}
      </AuthContext.Provider>
    );
  }
}

// LMS-970: AWS Cognito ID and access tokens expire after 1 hour
// Here with make a request to auth every 30 minutes to keep the connection alive
// Refer: https://docs.aws.amazon.com/cognito/latest/developerguide/limits.html#limits-hard
setInterval(async () => {
  try {
    await Auth.currentSession();
  } catch {
    window.location.href = `${config.app.secureUrl}${redirectLogin()}`;
  }
}, 1800000);

AuthProvider.displayName = 'AuthProvider';
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthProvider;
AuthProvider.contextType = SnackbarContext;
